// @ts-nocheck

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { notification } from "antd";
import { http } from "../../../engine/api/http";
import {
  levels,
  roles,
  statusPrestationsValues,
  subStatus,
  subStatusPrestations,
} from "../../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { useContext, useEffect, useState } from "react";
import routes from "../../../engine/routes";
import { useNavigate } from "react-router-dom";
import dayjs from "../../../utils/dayjs";
import _ from "lodash";
import { ansi } from "chalk";
import { router } from "../../../engine/router/AppRouter";
import {
  confirmAnswerYesPrestation,
  modalModificationPrestationFinishPending,
  modalModificationPrestationPending,
  modalPrestationChangedReload,
} from "../../../shared/components/Modals/modals";
import { initialStatePrestation } from "./Prestation.default.state";
import { userHasLevel } from "../../../engine/AuthRoute";
import { UserContext } from "../../../engine/context/User.context";

// A save of the whole booking sends the version this page has (0: the booking never changed since the release).
// It goes under its own key: a page opened before the release has none and is saved as before.
const withEditVersion = prestation => ({
  ...prestation,
  loadedEditVersion: prestation.editVersion ?? 0,
});

let reloadRequested = false;

const rejectFailedSave = (err, { rejectWithValue }) => {
  // 409: the booking changed since this page got it (another person, an interpreter's answer, the expiry).
  // Nothing was saved; the page is reloaded so the next change starts from the current booking.
  if (
    err?.response?.status === 409 &&
    err.response.data?.code === "STALE_PRESTATION"
  ) {
    if (!reloadRequested) {
      reloadRequested = true;
      modalPrestationChangedReload({ onOk: () => window.location.reload() });
    }
    return rejectWithValue({ stale: true });
  }

  // 401 and 422 are already shown by the interceptor; a server error or a lost connection would show nothing.
  if (err?.isAxiosError && (!err.response || err.response.status >= 500)) {
    notification.error({
      message:
        "Une erreur est survenue et la modification n'a peut-être pas été enregistrée. Veuillez recharger la page avant de réessayer.",
    });
  }
  throw err;
};

export const getPrestation = createAsyncThunk(
  "prestation/getPrestation",
  async (prestationId, thunkAPI) => {
    const res = await http.get("/prestations/" + prestationId);

    // thunkAPI.dispatch(getInterpretesSearch());

    // Accessing headers from the response
    const headers = res.headers;

    // Example: Accessing a specific header, e.g., 'X-Custom-Header'
    const customHeader = headers["X-by-ref"];

    return res.data;
  },
);

export const getEstimationCost = createAsyncThunk(
  "prestation/getEstimationCost",
  async (prestationId, thunkAPI) => {
    const res = await http.get(`/prestations/${prestationId}/cost`);

    return res.data;
  },
);

export const getInterpretesSearch = createAsyncThunk(
  "prestation/getInterpretesSearch",
  async (prestationId, thunkAPI) => {
    const prestation = thunkAPI.getState().prestation.prestation;

    const res = await http.get(`/prestations/${prestationId}/search`);

    // const res = await http.get(`users?role=${roles.interprete}`);

    return res.data;
  },
);

export const postAppelInterpretes = createAsyncThunk(
  "prestation/postAppelInterpretes",
  async (prestationId, thunkAPI) => {
    const { _id } = thunkAPI.getState().prestation.prestation;

    // const res = await http.get(`/prestations/${prestationId}/search`);

    const res = await http.post(`prestations/${_id}/appel_interpretes`);

    return res.data;
  },
);

export const postAnswerChangeApprovedRefused = createAsyncThunk(
  "prestation/postAnswerChange",
  async (answer, thunkAPI) => {
    const { _id } = thunkAPI.getState().prestation.prestation;

    const res = await http.post(`prestations/${_id}/interprete_answer_change`, {
      answer,
    });

    return res.data;
  },
);

export const postManuelPrestation = createAsyncThunk(
  "prestation/postManuelPrestation",
  async (prestationId, thunkAPI) => {
    const { _id } = thunkAPI.getState().prestation.prestation;

    const res = await http.post(`prestations/${_id}/confirm_planned`);

    return res.data;
  },
);

export const postInterpretesAnswer = createAsyncThunk(
  "prestation/postInterpretesAnswer",
  async ({ answer, interpreteId, invitationToken }, thunkAPI) => {
    const { _id } = thunkAPI.getState().prestation.prestation;

    // const res = await http.get(`/prestations/${prestationId}/search`);

    try {
      const res = await http.post(`prestations/${_id}/answer`, {
        answer,
        interpreteId,
        invitationToken,
      });

      return res.data;
    } catch (err) {
      // 401 and 422 are already shown by the interceptor. The answer may already be saved
      // (the server saves it before the e-mails), so the message does not say it was lost.
      if (!err?.response || err.response.status >= 500) {
        notification.error({
          message:
            "Une erreur est survenue et votre réponse n'a pas pu être confirmée. Veuillez recharger la page avant de réessayer.",
        });
      }
      throw err;
    }
  },
);

export const deletePrestation = createAsyncThunk(
  "prestation/deletePrestation",
  async (payload, thunkAPI) => {
    const { _id } = thunkAPI.getState().prestation.prestation;

    const res = await http.delete(`prestations/${_id}`);

    return res.data;
  },
);
export const duplicatePrestation = createAsyncThunk(
  "prestation/duplicatePrestation",
  async (payload, thunkAPI) => {
    const { _id } = thunkAPI.getState().prestation.prestation;

    const res = await http.post(`prestations/${_id}/duplicate`);

    return res.data;
  },
);

export const updatePrestation = createAsyncThunk(
  "prestation/update",
  async (payload, thunkAPI, state) => {
    const prestation = thunkAPI.getState().prestation.prestation;

    try {
      if (prestation._id !== undefined) {
        if (
          prestation.status === statusPrestationsValues.planned ||
          prestation.status === statusPrestationsValues.waiting
        ) {
          let showModalConfirmUpdate;
          if (prestation.status === statusPrestationsValues.waiting) {
            showModalConfirmUpdate = true;
          } else {
            showModalConfirmUpdate = await new Promise((resolve, reject) => {
              modalModificationPrestationPending({
                onOk: async () => {
                  resolve(true);
                },
                onCancel: () => {
                  reject(new Error("Update canceled."));
                },
              });
            });
          }

          if (showModalConfirmUpdate) {
            const resapproval = await http.patch(
              `/prestations/${prestation._id}/approval`,
              withEditVersion(prestation),
            );
            notification.success({
              message: `Demande de modification enregistrée`,
            });

            const res = await http.get("/prestations/" + prestation._id);

            return res.data;
          }
        } else if (
          [
            statusPrestationsValues.finish,
            statusPrestationsValues.finish_notsigned,
            statusPrestationsValues.finish_permanance,
            statusPrestationsValues.cancel,
            statusPrestationsValues.cancelClient,
            statusPrestationsValues.cancelInterprete,
          ].includes(prestation.status) ||
          [subStatus.interprete, subStatus.institution].includes(
            prestation.subStatus,
          )
        ) {
          const showModalConfirmFinishUpdate = await new Promise(
            (resolve, reject) => {
              modalModificationPrestationFinishPending({
                onOk: async () => {
                  resolve(true);
                },
                onCancel: () => {
                  reject(new Error("Update canceled."));
                },
              });
            },
          );

          if (showModalConfirmFinishUpdate) {
            const resPatch = await http.patch(
              "/prestations/" + prestation._id,
              withEditVersion(prestation),
            );

            // let syncBill;

            // syncBill = await http.post(`/bills/upsertOneBillOnePrestation`, {
            //   prestation: prestation._id,
            // });

            const res = await http.get("/prestations/" + prestation._id);

            notification.success({
              message: `Prestation Modifiée`,
            });

            return res.data;
          }
        } else {
          const res = await http.patch(
            "/prestations/" + prestation._id,
            withEditVersion(prestation),
          );
          notification.success({
            message: `Prestation Modifiée`,
          });
          return res.data;
        }
      } else {
        const res = await http.post("/prestations/new", prestation);
        // thunkAPI.dispatch(updatePrestationOnList(res.data));
        notification.success({
          message: `Prestation Créée`,
        });
        return res.data;
      }
    } catch (err) {
      return rejectFailedSave(err, thunkAPI);
    }
  },
);

export const updateAdminPrestation = createAsyncThunk(
  "prestation/updateAdminPrestation",
  async (payload, thunkAPI, state) => {
    const prestation = thunkAPI.getState().prestation.prestation;
    const sent = withEditVersion(prestation);

    try {
      const resPatch = await http.patch("/prestations/" + prestation._id, sent);

      notification.success({
        message: `Prestation modifiée`,
      });

      return {
        _id: prestation._id,
        sentVersion: sent.loadedEditVersion,
        editVersion: resPatch.data?.editVersion,
      };
    } catch (err) {
      return rejectFailedSave(err, thunkAPI);
    }
  },
);

// "Modifier commentaire" saves only the permanence comment, not the whole booking: a change made since the
// page was loaded (an interpreter's answer, another person's save) neither blocks it nor gets overwritten.
// `previous` is the comment the person started from; if someone else changed it, nothing is saved and the
// current comment comes back (rejected with `staleComment`).
export const updateCommentAdminPrestation = createAsyncThunk(
  "prestation/updateCommentAdminPrestation",
  async ({ admin, previous }, { getState, rejectWithValue }) => {
    const { _id, editVersion } = getState().prestation.prestation;

    try {
      const res = await http.patch(`/prestations/${_id}/comment`, {
        admin,
        previous,
      });

      notification.success({
        message: `Commentaire permanence enregistré`,
      });

      return { ...res.data, _id, sentVersion: editVersion ?? 0 };
    } catch (err) {
      if (
        err?.response?.status === 409 &&
        err.response.data?.code === "STALE_COMMENT"
      ) {
        return rejectWithValue({
          _id,
          staleComment: true,
          comment: err.response.data.comment ?? "",
        });
      }

      // A 401 is already shown by the interceptor.
      if (err?.response?.status !== 401) {
        notification.error({
          message:
            "Le commentaire n'a pas été enregistré. Votre texte est conservé, vous pouvez réessayer.",
          description: err?.response?.data?.message,
        });
      }
      return rejectWithValue({ _id });
    }
  },
);

export const updateControlPrestation = createAsyncThunk(
  "prestation/updateControlPrestation",
  async (payload, thunkAPI, state) => {
    const prestation = thunkAPI.getState().prestation.prestation;

    const res = await http.patch(
      "/prestations/" + prestation._id + "/control",
      prestation,
    );

    notification.success({
      message: `Prestation modifiée`,
    });

    return res.data;
  },
);

export const followPrestation = createAsyncThunk(
  "prestation/follow",
  async (payload, thunkAPI, state) => {
    const res = await http.post(
      `/prestations/${payload.followedPrestation}/follow`,
      payload,
    );
    notification.success({
      message: `Prestation de suivi créée`,
    });
    return res.data;
  },
);

export const cancelPrestation = createAsyncThunk(
  "prestation/cancel",
  async (payload, thunkAPI, state) => {
    const prestation = thunkAPI.getState().prestation.prestation;

    try {
      const resCancel = await http.post(
        `/prestations/${prestation._id}/cancel`,
        withEditVersion(prestation),
      );
      notification.success({
        message: `Prestation annulée`,
      });

      const res = await http.get("/prestations/" + prestation._id);

      return res.data;
    } catch (err) {
      return rejectFailedSave(err, thunkAPI);
    }
  },
);

export const finishPrestation = createAsyncThunk(
  "prestation/finishPrestation",
  async (payload, thunkAPI, state) => {
    const prestation = thunkAPI.getState().prestation.prestation;

    try {
      const res = await http.post(
        `/prestations/${prestation._id}/finish`,
        withEditVersion(prestation),
      );
      notification.success({
        message: `Prestation terminée`,
      });
      return res.data;
    } catch (err) {
      return rejectFailedSave(err, thunkAPI);
    }
  },
);

export const setLoadingAsync = createAsyncThunk(
  "prestation/setLoadingAsync",
  async (payload, thunkAPI, state) => {
    return payload;
  },
);

const prestationSlice = createSlice({
  name: "prestation",
  initialState: initialStatePrestation,
  reducers: {
    clearPrestation: state => {
      return initialStatePrestation;
    },
    dangerousUpdate: state => {
      state.dangerousUpdate = true;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    changeStatusInterprete: (state, action) => {
      let ref = state.prestation.interpretesStatus.find(
        e => e.interprete._id === action.payload._id,
      );

      if (ref.status === "selected") {
        state.prestation.interprete = null;
      }

      if (action.payload.status === "selected") {
        ref.selectedAt = dayjs().format();
      }

      ref.status = action.payload.status;
    },
    changePositionInterprete: (state, action) => {
      let ref = state.prestation.interpretesStatus.find(
        e => e.interprete._id === action.payload._id,
      );
      ref.position = action.payload.position;
    },
    setPrestation: (state, action) => {
      state.prestation = action.payload;
    },
    setPropertiesPrestation: (state, action) => {
      /*
      * In this example, the customizer function is checking if the property is an array. If it is, the value from action.payload (srcValue) will replace the value in state.prestation (objValue), effectively making action.payload the "master" object. If the property is not an array, Lodash will default to its normal merge behavior.
      This way, you'll still get a deep merge for nested objects, but you can customize how arrays (or other types if you need) are handled.
      * */
      const customizer = (objValue, srcValue) => {
        if (_.isArray(objValue)) {
          return srcValue;
        }
      };

      let merged = _.mergeWith(
        {},
        state.prestation,
        action.payload,
        customizer,
      );

      state.prestation = merged;
    },
    setSelectedInterprete: (state, action) => {
      // All set to not invited
      state.prestation.interpretesStatus = state.prestation.interpretesStatus.map(
        e => {
          if (e.status === "selected" || e.status === "invited") {
            e.status = "not_invited";
          }

          e.selectedAt = null;

          return e;
        },
      );

      state.prestation.interprete = action.payload;
    },
  },
  extraReducers: {
    [getPrestation.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [getEstimationCost.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [getEstimationCost.fulfilled]: (state, { payload }) => {
      state.cost = payload;
      state.loading = false;
    },
    [setLoadingAsync.pending]: (state, { payload }) => {},
    [setLoadingAsync.fulfilled]: (state, { payload }) => {
      state.loading = payload;
    },
    [deletePrestation.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [deletePrestation.fulfilled]: (state, { payload }) => {
      notification.success({
        message: `Supprimé avec succès`,
      });
      router.navigate("/", { replace: true });
    },
    [getInterpretesSearch.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [getInterpretesSearch.fulfilled]: (state, { payload }) => {
      state.prestation.interpretesStatus = payload.interpretesStatus;
      state.prestation.searchInterpretes = payload.searchInterpretes;
      state.loading = false;
      state.interpretesHasSearch = true;
    },
    [getPrestation.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.ready = true;
      state.prestation = payload;
    },
    [updatePrestation.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [updatePrestation.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [updatePrestation.fulfilled]: (state, { payload }) => {
      state.prestation = payload;
      state.cost = null;
      state.loading = false;
    },
    [updateAdminPrestation.pending]: (state, { payload }) => {
      state.loadingEdit = true;
    },
    [updateControlPrestation.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [updateControlPrestation.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.prestation = payload;
    },
    [updateAdminPrestation.rejected]: (state, { payload }) => {
      state.loadingEdit = false;
    },
    [updateAdminPrestation.fulfilled]: (state, { payload }) => {
      state.loadingEdit = false;
      // The page's copy is what it just saved: it takes the version of its own save. If the booking
      // changed again in between, the copy keeps its old version and the next save reloads the page.
      if (
        payload?._id === state.prestation._id &&
        payload?.editVersion === payload?.sentVersion + 1
      ) {
        state.prestation.editVersion = payload.editVersion;
      }
    },
    [updateCommentAdminPrestation.pending]: (state, { payload }) => {
      state.loadingEdit = true;
    },
    [updateCommentAdminPrestation.rejected]: (state, { payload }) => {
      state.loadingEdit = false;
      // The page shows the comment that is saved now, the one the other person wrote.
      if (payload?.staleComment && payload._id === state.prestation._id) {
        state.prestation.comment = {
          ...state.prestation.comment,
          admin: payload.comment,
        };
      }
    },
    [updateCommentAdminPrestation.fulfilled]: (state, { payload }) => {
      state.loadingEdit = false;
      if (payload?._id !== state.prestation._id) {
        return;
      }
      state.prestation.comment = {
        ...state.prestation.comment,
        admin: payload.comment?.admin ?? "",
      };
      // Same rule as a whole save: the version is taken only if nothing else changed the booking in between,
      // otherwise the page keeps its old version and its next whole save reloads it.
      if (payload.editVersion === payload.sentVersion + 1) {
        state.prestation.editVersion = payload.editVersion;
      }
    },
    [cancelPrestation.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [cancelPrestation.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [cancelPrestation.fulfilled]: (state, { payload }) => {
      state.prestation = payload;
      state.loading = false;
    },
    [followPrestation.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [followPrestation.fulfilled]: (state, { payload }) => {
      router.navigate(routes.prestations(payload._id).detail.path, {
        replace: true,
      });

      state.loading = false;
    },
    [duplicatePrestation.pending]: (state, { payload }) => {
      state.loading = true;
      state.ready = false;
    },
    [duplicatePrestation.rejected]: (state, action) => {
      state.loading = true;
    },
    [duplicatePrestation.fulfilled]: (state, { payload }) => {
      // state = payload;
      // state.prestation = payload;

      // state = initialStatePrestation;
      // return initialStatePrestation;
      router.navigate(routes.prestations(payload._id).detail.path, {
        replace: true,
        state: { force: true },
      });


      // return initialStatePrestation;

      state.loading = false;
      state.prestation._id = payload._id;
    },
    [finishPrestation.pending]: (state, { payload }) => {},
    [finishPrestation.fulfilled]: (state, { payload }) => {
      state.prestation = payload;
    },
    [postInterpretesAnswer.pending]: (state, { payload }) => {
      state.loadingAnswer = true;
    },
    [postInterpretesAnswer.rejected]: (state, response) => {
      state.loadingAnswer = false;
    },
    [postInterpretesAnswer.fulfilled]: (state, { payload }) => {
      if (payload.finalAnswer) {
        confirmAnswerYesPrestation({
          onCancel: () => {
            router.navigate("/", { replace: true });
          },
        });
      } else {
        notification.success({
          message: `Merci pour votre réponse`,
        });
        router.navigate("/", { replace: true });
      }

      state.prestation = payload.prestation;
      state.loadingAnswer = false;
    },
    [getPrestation.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [postAppelInterpretes.pending]: (state, { payload }) => {
      state.loadingAppel = true;
    },
    [postAppelInterpretes.fulfilled]: (state, { payload }) => {
      notification.success({
        message: `Prestation confirmée`,
        description: `${payload.sent} emails ont été envoyés`,
      });
      state.prestation = payload.prestation;
      state.loadingAppel = false;
    },
    [postAnswerChangeApprovedRefused.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [postAnswerChangeApprovedRefused.rejected]: (state, { payload }) => {
      state.loading = false;
    },
    [postAnswerChangeApprovedRefused.fulfilled]: (state, { payload }) => {
      notification.success({
        message: `Réponse enregistrée`,
      });
      state.prestation = payload.prestation;
      state.loading = false;
    },
    [postManuelPrestation.pending]: (state, { payload }) => {
      state.loading = true;
    },
    [postManuelPrestation.fulfilled]: (state, { payload }) => {
      notification.success({
        message: `Prestation confirmée et prévue`,
      });
      state.prestation = payload.prestation;
      state.loading = false;
    },
  },
});

export const initPrestationSlice = ({ id }) => {
  const dispatch = useDispatch();

  const { user } = useContext(UserContext);

  const [ready, setReady] = useState(id === "new");

  const { prestation, loading, deleteSuccess } = useSelector(
    state => state.prestation,
  );
  const navigate = useNavigate();

  const hasLevelNew = userHasLevel(routes.prestations("new").new);

  const { _id } = prestation;

  const init = async () => {
    try {
      if (hasLevelNew === false && id === "new") {
        await clear();
        navigate(routes.prestations().list.path, {
          replace: true,
        });
      }

      if (user?.institution?.readOnlyPrestation === true && id === "new") {
        await clear();
        navigate(routes.prestations().list.path, {
          replace: true,
        });
      }

      if (prestation._id !== id && id !== "new") {
        if (prestation._id !== undefined) {
          await clear();
        }
        console.log(getPrestation());
        await dispatch(getPrestation(id));
      }
    } catch (e) {
      console.log(e);
    }
  };

  const clear = async () => {
    await dispatch(clearPrestation());
  };

  useEffect(() => {
    if (prestation._id !== undefined && id === "new") {
      navigate(routes.prestations(prestation._id).detailInterpretes.path, {
        replace: true,
      });
    }
    init();

    if (
      prestation._id !== undefined &&
      id === prestation._id &&
      loading !== true
    ) {
      setReady(true);
    }
  }, [prestation]);

  const resetToNew = async () => {
    if (id === "new" && prestation._id !== undefined && prestation._id !== id) {
      // Reset when go to a new one
      await clear();
      navigate(routes.prestations().new.path, {
        replace: true,
      });
    }
  };

  useEffect(() => {
    init();

    resetToNew();

    return () => {
      // clear();
    };
  }, []);

  return { init, clear, ready };
};

export const {
  setLoading,
  changeStatusInterprete,
  setSelectedInterprete,
  clearPrestation,
  changePositionInterprete,
  dangerousUpdate,
  setPropertiesPrestation,
  setPrestation,
} = prestationSlice.actions;

export const prestationReducer = prestationSlice.reducer;
